import React, { useEffect } from 'react';
import { team, engagements } from '../mock';
import * as Icons from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { useLocation, Link } from 'react-router-dom';

export const TMCPage = () => {
  const location = useLocation();
  
  // Gestion des ancres internes
  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  const scrollToSection = (sectionId) => {
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F7F6]">
      {/* Hero Section avec image de fond institutionnelle */}
      <section className="relative min-h-[450px] flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80" 
            alt="The MajordHome Conciergerie - Bureau professionnel" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#153254]/95 via-[#153254]/85 to-[#122b26]/75"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl py-24">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white">
              The MajordHome Conciergerie
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-2xl mb-8">
              Votre partenaire de confiance pour tous vos services à domicile
            </p>
            
            {/* Navigation interne */}
            <div className="flex flex-wrap gap-3">
              <button 
                onClick={() => scrollToSection('#histoire')}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-colors backdrop-blur-sm"
              >
                Notre Histoire
              </button>
              <button 
                onClick={() => scrollToSection('#equipe')}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-colors backdrop-blur-sm"
              >
                Notre Équipe
              </button>
              <button 
                onClick={() => scrollToSection('#engagements')}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-colors backdrop-blur-sm"
              >
                Nos Engagements
              </button>
              <button 
                onClick={() => scrollToSection('#actualites')}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-colors backdrop-blur-sm"
              >
                Actualités
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Histoire */}
      <section id="histoire" className="py-24 bg-white scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-[#153254]">Notre Histoire</h2>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  <strong className="text-[#0A2320]">L'entreprise a été fondée par Franck, Josué et Christie.</strong>
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The MajordHome Conciergerie est née de la volonté de simplifier le quotidien des particuliers 
                  et des professionnels en proposant des services à domicile de qualité, accessibles et fiables.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Fort d'une expérience solide dans le secteur du service à la personne, 
                  notre entreprise s'est rapidement imposée comme un acteur de confiance dans la région de Méru 
                  et ses environs.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Aujourd'hui, The MajordHome Conciergerie c'est une équipe engagée, des prestations diversifiées 
                  et un engagement fort : faciliter votre vie au quotidien avec professionnalisme et bienveillance.
                </p>
              </div>
              <div className="relative">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_a72f3521-3152-44ec-800d-97dc3c085d7c/artifacts/vkime6zd_GROUPE%20%281%29.png"
                    alt="L'équipe The MajordHome - Christie, Franck et Josué"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#D4AF37]/20 rounded-2xl -z-10"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Équipe */}
      <section id="equipe" className="py-24 bg-[#F4F7F6] scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-[#153254]">Notre Équipe</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {team.map(member => (
                <Card key={member.id} className="bg-white border-0 shadow-lg hover:shadow-xl transition-shadow overflow-hidden rounded-3xl">
                  <CardContent className="p-0 flex flex-col">
                    {/* Photo - garde ses proportions naturelles */}
                    <img 
                      src={member.photo} 
                      alt={member.name}
                      className="w-full"
                    />
                    {/* Contenu texte sur fond blanc sans bordure */}
                    <div className="p-6">
                      <h3 className="text-xl font-bold mb-1 text-[#153254]">{member.name}</h3>
                      <p className="text-[#D4AF37] font-semibold mb-3">{member.role}</p>
                      <p className="text-gray-700 leading-relaxed text-sm">{member.description}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Nos Engagements */}
      <section id="engagements" className="py-24 bg-white scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-[#153254]">Nos Engagements</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {engagements.map(engagement => {
                const IconComponent = Icons[engagement.icon];
                return (
                  <Card key={engagement.id} className="bg-white border-0 shadow-md hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#153254] to-[#122b26] rounded-full flex items-center justify-center mb-4">
                        {IconComponent && <IconComponent className="h-8 w-8 text-white" />}
                      </div>
                      <h3 className="text-xl font-bold mb-3 text-[#153254]">{engagement.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{engagement.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Choisir TMC */}
      <section className="py-24 bg-[#F4F7F6]">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-[#153254]">Choisir TMC, c'est quoi ?</h2>
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#153254]/10 rounded-full flex items-center justify-center">
                    <Icons.CheckCircle className="h-6 w-6 text-[#153254]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-[#153254]">Un service clé en main</h3>
                    <p className="text-gray-700">De la prise de contact à la réalisation de la prestation, nous gérons tout pour vous.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#153254]/10 rounded-full flex items-center justify-center">
                    <Icons.Users className="h-6 w-6 text-[#153254]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-[#153254]">Des intervenants de confiance</h3>
                    <p className="text-gray-700">Tous nos collaborateurs sont formés, assurés et suivis régulièrement.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#153254]/10 rounded-full flex items-center justify-center">
                    <Icons.Award className="h-6 w-6 text-[#153254]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-[#153254]">50% de crédit d'impôt immédiat</h3>
                    <p className="text-gray-700">Bénéficiez de l'avance immédiate sans attendre votre déclaration fiscale.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#153254]/10 rounded-full flex items-center justify-center">
                    <Icons.MapPin className="h-6 w-6 text-[#153254]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-[#153254]">Proximité et réactivité</h3>
                    <p className="text-gray-700">Basés à Méru (60110), nous intervenons rapidement dans un rayon de 50 km.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Actualités */}
      <section id="actualites" className="py-24 bg-white scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-[#153254]">Nos Actualités</h2>
            <div className="bg-gradient-to-r from-[#153254]/10 to-[#122b26]/10 rounded-2xl p-12">
              <div className="flex items-start gap-6">
                <Icons.Calendar className="h-12 w-12 text-[#153254]/60 flex-shrink-0" />
                <div>
                  <p className="text-xl text-[#153254]/80 mb-4">
                    Restez informés de nos dernières nouveautés, services et offres spéciales.
                  </p>
                  <p className="text-gray-600">
                    Bientôt disponible : nouveaux services, jeux concours, avant/après, et informations pratiques.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-24 bg-gradient-to-br from-[#153254] to-[#122b26] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Prêt à simplifier votre quotidien ?</h2>
            <p className="text-xl mb-8 text-white max-w-2xl mx-auto">Contactez-nous pour un devis personnalisé</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/devis-premium">
                <Button size="lg" className="bg-[#D4AF37] hover:bg-[#c9a030] text-white font-semibold text-base px-8 py-6 rounded-xl shadow-lg">
                  Demander un devis
                </Button>
              </Link>
              <Link to="/#contact">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#153254] font-semibold text-base px-8 py-6 rounded-xl">
                  Nous contacter
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
